import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import moment from 'moment';
import classnames from 'classnames/bind';
import logger from '@adc/logger';
import { getCssColorFromColorName } from '../../../utils/colors';
import { ColorNames } from '../../../constants/colorVars';
import Text from '../text';
import AnimatedProgressBar from '../animatedProgressBar';
import useElementDimensions from '../../../hooks/useElementDimensions';
import styles from './styles.scss';
const cx = classnames.bind(styles);
const PROGRESS_BAR_HEIGHT = 10;
const PROGRESS_BAR_NUDGE = PROGRESS_BAR_HEIGHT / 2;
const Tick = ({ color, left }) => (_jsx("div", { style: { backgroundColor: color, left }, className: cx('tick') }));
const ProgressTimeline = ({ leftLabel, rightLabel, accentColor = ColorNames.Info, markerLabel, startDate, endDate, endedLabel = 'Ended', className, style, }) => {
    const [markerLabelLeft, setMarkerLabelLeft] = useState(0);
    const [markerTickLeft, setMarkerTickLeft] = useState(0);
    const markerRef = useRef(null);
    const markerContainerRef = useRef(null);
    const daysLeft = Math.ceil(moment(endDate).diff(moment(), 'days', true));
    const datesAreInvalid = isNaN(daysLeft) || moment(startDate).isAfter(endDate);
    const isCompleted = moment().isSameOrAfter(endDate) && !datesAreInvalid;
    if (datesAreInvalid) {
        logger.error(`Invalid dates provided to ProgressTimeline: startDate=${moment(startDate).format('MMMM Do, YYYY')}, endDate=${moment(endDate).format('MMMM Do, YYYY')}`);
    }
    const percent = datesAreInvalid
        ? 0
        : moment().isAfter(endDate)
            ? 100
            : Math.min(100, (moment().diff(startDate, 'minutes') /
                moment(endDate).diff(startDate, 'minutes')) *
                100);
    const formattedEndDate = moment(endDate).format('MM/DD');
    const markerContainerDimensions = useElementDimensions(markerContainerRef);
    const markerDimensions = useElementDimensions(markerRef);
    const _markerLabel = (days) => {
        if (moment().isBefore(startDate))
            return `Starts ${moment(startDate).format('MM/DD')}`;
        return markerLabel(days);
    };
    useEffect(() => {
        if (markerDimensions && markerContainerDimensions) {
            setMarkerLabelLeft(Math.max(0, Math.min(markerContainerDimensions.width - markerDimensions.width - 3, (markerContainerDimensions.width * percent) / 100 -
                markerDimensions.width / 2 -
                PROGRESS_BAR_NUDGE)));
            setMarkerTickLeft(Math.max(0, (markerContainerDimensions.width * percent) / 100 - PROGRESS_BAR_NUDGE));
        }
    }, [percent, markerDimensions, markerContainerDimensions]);
    const backgroundColor = getCssColorFromColorName(accentColor);
    const endDateColor = getCssColorFromColorName(ColorNames.Success);
    return (_jsxs("div", { className: cx('progress-timeline', className), style: style, "data-elm-id": 'progress_timeline', children: [_jsxs("div", { className: cx('labels'), children: [_jsx(Text.Body, { as: 'div', size: 'small', weight: 'heavy', color: ColorNames.Gray, children: leftLabel }), _jsx(Text.Body, { as: 'div', size: 'small', weight: 'heavy', color: ColorNames.Gray, style: { textAlign: 'right' }, children: rightLabel })] }), _jsxs("div", { className: cx('progress-bar'), children: [_jsx(AnimatedProgressBar, { color: accentColor, percent: percent, rounded: true }), _jsx("div", { className: cx('dot', { completed: isCompleted }) })] }), datesAreInvalid ? null : (_jsxs("div", { className: cx('markers'), children: [_jsx("div", { ref: markerContainerRef, className: cx('left'), children: percent !== 100 && (_jsxs(_Fragment, { children: [_jsx(Tick, { color: backgroundColor, left: markerTickLeft }), _jsx(Text.Body, { as: 'div', size: 'extraSmall', weight: 'heavy', color: ColorNames.Gray, style: { left: markerLabelLeft }, className: cx('marker-label'), ref: markerRef, children: _markerLabel(daysLeft) })] })) }), _jsxs("div", { className: cx('right'), children: [_jsx(Tick, { color: endDateColor, left: 4 }), _jsx(Text.Body, { className: cx('end-date'), as: 'div', size: 'extraSmall', weight: 'heavy', color: ColorNames.Gray, style: {
                                    right: percent < 85 || percent === 100 ? 0 : 'initial',
                                }, children: percent === 100 ? endedLabel : formattedEndDate })] })] }))] }));
};
export default ProgressTimeline;
//# sourceMappingURL=index.js.map